import { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Cpu,
  HardHat,
  TrendingUp,
  Landmark,
  MapPin,
  GraduationCap,
  Zap,
  ShieldCheck,
  ArrowRight,
  ExternalLink,
} from 'lucide-react';
import SeoHead from '@/components/shared/SeoHead';
import PageHero, { HeroAccent, HeroStatTile } from '@/components/shared/PageHero';
import { LeaderboardAd, InContentAd, SidebarAd } from '@/components/shared/AdSense';
import OccupationLink from '@/components/shared/OccupationLink';
import AffiliateCta from '@/components/shared/AffiliateCta';
import NewsletterSignup from '@/components/shared/NewsletterSignup';
import { supabase } from '@/integrations/supabase/client';
import { STATE_MAPPINGS } from '@/lib/stateUtils';
import { isCrawlableSalaryLink } from '@/lib/seoIndexing';
import {
  AI_ECONOMY_OCCUPATIONS,
  AI_ECONOMY_STATS,
  type AiEconomyOccupation,
  type AiEconomyTier,
} from '@/lib/aiEconomyConfig';
import {
  generateWebPageSchema,
  generateFAQPageSchema,
  generateBreadcrumbSchema,
  generateMultipleSchemas,
} from '@/lib/schema';

interface ApiOccupation {
  soc: string;
  occ_title: string;
  annual_mean_wage?: number;
  annual_median_wage?: number;
  total_employment?: number;
}

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(amount);

const AIEconomyJobs = () => {
  const { location } = useParams<{ location?: string }>();

  const state = location ? STATE_MAPPINGS.find((s) => s.slug === location) : undefined;
  const areaName = state?.name || 'United States';
  const isStatePage = !!state;

  // Live wages via the ai_economy type on api-top-occupations. The curated
  // config supplies BLS national medians as a fallback so the page is never
  // empty while the API loads or if the type isn't deployed yet.
  const { data: liveData } = useQuery({
    queryKey: ['ai-economy-occupations', areaName],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke('api-top-occupations', {
        body: { area: areaName, type: 'ai_economy', limit: 30 },
      });
      if (error) throw error;
      return (data?.occupations || []) as ApiOccupation[];
    },
    staleTime: 1000 * 60 * 60,
    retry: 1,
  });

  const wageBySoc = useMemo(() => {
    const map = new Map<string, ApiOccupation>();
    (liveData || []).forEach((occ) => {
      if (occ.soc) map.set(occ.soc, occ);
    });
    return map;
  }, [liveData]);

  const getWage = (occ: AiEconomyOccupation): { wage: number; isLive: boolean } => {
    const live = wageBySoc.get(occ.socCode);
    const wage = live?.annual_median_wage || live?.annual_mean_wage;
    if (wage && wage > 0) return { wage, isLive: true };
    return { wage: occ.medianWage2024, isLive: false };
  };

  const hasLiveWages = wageBySoc.size > 0;

  const tiers: Array<{
    tier: AiEconomyTier;
    heading: string;
    description: string;
    icon: typeof Cpu;
  }> = [
    {
      tier: 'ai_core',
      heading: 'AI Core Careers',
      description:
        'Occupations that build, secure, and manage AI systems. BLS projects computer and mathematical occupations to grow 10.1% through 2034 — more than three times the overall economy.',
      icon: Cpu,
    },
    {
      tier: 'ai_infrastructure',
      heading: 'AI Infrastructure Careers',
      description:
        'The trades and engineering roles building the data centers, power generation, and chip plants behind AI. Many pay $60k-$90k+ without a four-year degree, and the Department of Labor is expanding Registered Apprenticeships for them.',
      icon: HardHat,
    },
  ];

  const occupationsByTier = (tier: AiEconomyTier) =>
    AI_ECONOMY_OCCUPATIONS.filter((o) => o.tier === tier).sort(
      (a, b) => b.projectedGrowthPct - a.projectedGrowthPct
    );

  const faqItems = [
    {
      question: 'What is the AI economy?',
      answer:
        'The AI economy refers to the jobs, industries, and infrastructure growing around artificial intelligence. It spans two groups: careers that build and deploy AI systems (data scientists, software developers, security analysts) and careers that build the physical infrastructure behind AI (electricians, HVAC technicians, and power-line workers constructing data centers and grid capacity). The federal government has made both a workforce priority through the White House AI Action Plan and the Department of Labor\u2019s AI Registered Apprenticeship initiative.',
    },
    {
      question: 'What are the highest-paying AI economy jobs?',
      answer:
        'Based on BLS data, computer and information systems managers earn a median of about $171,000, computer hardware engineers about $155,000, and computer and information research scientists about $141,000. Among roles not requiring an advanced degree, software developers earn a median of about $133,000 and information security analysts about $125,000.',
    },
    {
      question: 'Can I work in the AI economy without a college degree?',
      answer:
        'Yes. The AI infrastructure buildout depends heavily on skilled trades that require an apprenticeship or certificate instead of a degree. Electrical power-line installers earn a median of about $92,500, electricians about $62,000, and wind turbine technicians \u2014 the fastest-growing occupation in America at nearly 50% projected growth \u2014 about $62,500. The Department of Labor is actively expanding Registered Apprenticeship programs in these fields.',
    },
    {
      question: 'How fast are AI jobs growing?',
      answer:
        'BLS projects computer and mathematical occupations to grow 10.1% from 2024 to 2034, more than triple the 3.1% growth projected for the overall economy. Data scientists are projected to grow 33.5% \u2014 the fourth-fastest of any occupation \u2014 and software developers are projected to add over 267,000 jobs, the largest numeric gain among AI-related roles.',
    },
    {
      question: 'Do AI skills increase your salary?',
      answer:
        'Yes, substantially. PwC\u2019s AI Jobs Barometer, based on over a billion job postings, found workers with AI skills command a wage premium of roughly 62% over peers in the same role without them \u2014 up from 25% in 2024. The premium applies across industries, not just tech.',
    },
  ];

  const baseUrl = 'https://wageatlas.com';
  const pagePath = isStatePage ? `/ai-economy-jobs/${state.slug}/` : '/ai-economy-jobs/';
  const pageUrl = `${baseUrl}${pagePath}`;
  const pageTitle = isStatePage
    ? `AI Economy Jobs in ${areaName}: Salaries & Careers | Wage Atlas`
    : 'AI Economy Jobs: Salaries for the Careers Powering AI | Wage Atlas';
  const pageDescription = isStatePage
    ? `AI economy jobs and salaries in ${areaName}: data scientists, software developers, electricians, data center trades and more, with real BLS wage data and growth projections.`
    : 'The careers powering the AI economy, with real BLS salaries and 2024-34 growth projections: AI core roles like data scientists, plus the infrastructure trades building data centers - many without a degree.';

  const itemListSchema = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: `AI Economy Occupations${isStatePage ? ` in ${areaName}` : ''}`,
    numberOfItems: AI_ECONOMY_OCCUPATIONS.length,
    itemListElement: AI_ECONOMY_OCCUPATIONS.map((occ, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: occ.title,
      url: `${baseUrl}/salary/${occ.slug}/${state?.slug || 'united-states'}/`,
    })),
  };

  const schemas = generateMultipleSchemas([
    generateWebPageSchema(pageTitle, pageDescription, pageUrl, {
      breadcrumbs: [
        { name: 'Home', url: `${baseUrl}/` },
        ...(isStatePage
          ? [
              { name: 'AI Economy Jobs', url: `${baseUrl}/ai-economy-jobs/` },
              { name: areaName, url: pageUrl },
            ]
          : [{ name: 'AI Economy Jobs', url: pageUrl }]),
      ],
    }),
    generateBreadcrumbSchema(
      [
        { name: 'Home', url: `${baseUrl}/` },
        { name: 'AI Economy Jobs', url: `${baseUrl}/ai-economy-jobs/` },
        ...(isStatePage ? [{ name: areaName, url: pageUrl }] : []),
      ],
      baseUrl
    ),
    generateFAQPageSchema(faqItems),
    itemListSchema,
  ]);

  return (
    <>
      <SeoHead title={pageTitle} description={pageDescription} canonical={pagePath} schemas={schemas} />

      <div className="min-h-screen bg-background">
        <PageHero
          badge={{ icon: Zap, label: 'The AI Economy' }}
          title={
            isStatePage ? (
              <>
                AI Economy Jobs in
                <HeroAccent> {areaName}</HeroAccent>
              </>
            ) : (
              <>
                The Jobs Powering the
                <HeroAccent> AI Economy</HeroAccent>
              </>
            )
          }
          subtitle={
            isStatePage
              ? `What AI-economy careers pay in ${areaName} - from data scientists to the trades building data centers. Official BLS wage data, updated with the latest release.`
              : 'From data scientists to the electricians wiring data centers: real salaries and official growth projections for the careers the government is betting on.'
          }
        >
          <div className="grid grid-cols-3 gap-4 max-w-2xl mx-auto">
            <HeroStatTile
              icon={TrendingUp}
              value={`+${AI_ECONOMY_STATS.dataScientistGrowthPct}%`}
              label="Data scientist growth by 2034"
            />
            <HeroStatTile
              icon={Cpu}
              value={`${Math.round(AI_ECONOMY_STATS.computerMathGrowthPct / AI_ECONOMY_STATS.totalEconomyGrowthPct)}x`}
              label="Tech job growth vs. overall economy"
            />
            <HeroStatTile
              icon={Zap}
              value={`+${AI_ECONOMY_STATS.aiWagePremiumPct}%`}
              label="Wage premium for AI skills (PwC)"
            />
          </div>
        </PageHero>

        <div className="container mx-auto px-4 py-8">
          <LeaderboardAd className="mb-8" />

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-10">
              {tiers.map(({ tier, heading, description, icon: TierIcon }) => (
                <section key={tier}>
                  <div className="flex items-center gap-3 mb-2">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      <TierIcon className="h-5 w-5 text-primary" />
                    </div>
                    <h2 className="text-2xl font-bold">{heading}</h2>
                  </div>
                  <p className="text-muted-foreground mb-6">{description}</p>

                  <div className="space-y-4">
                    {occupationsByTier(tier).map((occ) => {
                      const { wage, isLive } = getWage(occ);
                      return (
                        <Card key={occ.socCode} className="hover:shadow-md transition-shadow">
                          <CardContent>
                            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                              <div className="flex-1">
                                <h3 className="text-lg font-semibold mb-1">
                                  <OccupationLink
                                    occupation={{
                                      occ_title: occ.title,
                                      soc_code: occ.socCode,
                                      slug: occ.slug,
                                    }}
                                    location={state?.slug || 'united-states'}
                                    className="hover:text-primary/80"
                                  >
                                    {occ.title}
                                  </OccupationLink>
                                </h3>
                                <div className="flex flex-wrap items-center gap-2 mb-3">
                                  <Badge variant="secondary" className="text-xs">
                                    <GraduationCap className="h-3 w-3 mr-1" />
                                    {occ.education}
                                  </Badge>
                                  <span className="text-xs text-muted-foreground">SOC {occ.socCode}</span>
                                </div>
                                <p className="text-sm text-muted-foreground mb-3">{occ.note}</p>
                                <div className="grid grid-cols-2 gap-4 text-sm">
                                  <div>
                                    <div className="text-muted-foreground text-xs">
                                      Median Wage{isLive && isStatePage ? ` (${state.abbreviation})` : ''}
                                    </div>
                                    <div className="font-semibold text-lg">{formatCurrency(wage)}</div>
                                  </div>
                                  <div>
                                    <div className="text-muted-foreground text-xs">Projected Growth 2024-34</div>
                                    <div
                                      className={`font-semibold text-lg ${
                                        occ.projectedGrowthPct >= 15
                                          ? 'text-green-700 dark:text-green-400'
                                          : 'text-foreground'
                                      }`}
                                    >
                                      +{occ.projectedGrowthPct}%
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <Link
                                to={`/salary/${occ.slug}/${state?.slug || 'united-states'}/`}
                                {...(!isCrawlableSalaryLink(occ.slug, state?.slug || 'united-states') && {
                                  rel: 'nofollow',
                                })}
                                className="shrink-0"
                              >
                                <Button size="sm">
                                  View Salary
                                  <ArrowRight className="h-4 w-4 ml-1" />
                                </Button>
                              </Link>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>

                  {tier === 'ai_core' && (
                    <InContentAd />
                  )}
                </section>
              ))}

              {/* Government initiative section (E-E-A-T / topical authority) */}
              <section>
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 bg-primary/10 rounded-lg">
                    <Landmark className="h-5 w-5 text-primary" />
                  </div>
                  <h2 className="text-2xl font-bold">What the Government Is Doing</h2>
                </div>
                <Card>
                  <CardContent className="space-y-4 text-sm text-muted-foreground">
                    <p>
                      Preparing workers for the AI economy is now official federal policy. Three initiatives
                      shape where the jobs &mdash; and the training money &mdash; are going:
                    </p>
                    <ul className="space-y-3">
                      <li>
                        <strong className="text-foreground">The White House AI Action Plan</strong> directs
                        federal agencies to identify high-priority occupations essential to AI infrastructure
                        and expand training pipelines for them, from general education to Registered
                        Apprenticeships.{' '}
                        <a
                          href="https://www.ai.gov/action-plan"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary hover:underline inline-flex items-center gap-1"
                        >
                          ai.gov/action-plan <ExternalLink className="h-3 w-3" />
                        </a>
                      </li>
                      <li>
                        <strong className="text-foreground">
                          The Department of Labor&rsquo;s AI Registered Apprenticeship initiative
                        </strong>{' '}
                        (launched 2026) funds AI skills training inside apprenticeship programs, focused on
                        data centers, telecommunications, and advanced manufacturing &mdash; the exact trades in
                        our AI infrastructure list above.{' '}
                        <a
                          href="https://www.dol.gov/agencies/eta/apprenticeship"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary hover:underline inline-flex items-center gap-1"
                        >
                          dol.gov <ExternalLink className="h-3 w-3" />
                        </a>
                      </li>
                      <li>
                        <strong className="text-foreground">BLS Employment Projections (2024-34)</strong>{' '}
                        explicitly name demand for AI-based systems as a primary driver of job growth in
                        computer and mathematical occupations &mdash; the official data behind every number on
                        this page.
                      </li>
                    </ul>
                  </CardContent>
                </Card>
              </section>

              {/* Browse by state */}
              {!isStatePage && (
                <section>
                  <div className="flex items-center gap-3 mb-2">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      <MapPin className="h-5 w-5 text-primary" />
                    </div>
                    <h2 className="text-2xl font-bold">AI Economy Jobs by State</h2>
                  </div>
                  <p className="text-muted-foreground mb-6">
                    Wages for AI-economy careers vary widely by state. See what these jobs pay where you live.
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {STATE_MAPPINGS.map((s) => (
                      <Link
                        key={s.slug}
                        to={`/ai-economy-jobs/${s.slug}/`}
                        className="p-2 text-sm rounded hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
                      >
                        {s.name}
                      </Link>
                    ))}
                  </div>
                </section>
              )}

              {isStatePage && (
                <section>
                  <Card>
                    <CardContent className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                      <div>
                        <h2 className="text-lg font-semibold mb-1">Compare with the national picture</h2>
                        <p className="text-sm text-muted-foreground">
                          See national salaries and growth projections for every AI-economy career.
                        </p>
                      </div>
                      <Link to="/ai-economy-jobs/">
                        <Button variant="outline">
                          National AI Economy Jobs
                          <ArrowRight className="h-4 w-4 ml-1" />
                        </Button>
                      </Link>
                    </CardContent>
                  </Card>
                </section>
              )}

              {/* FAQ (mirrors FAQPage JSON-LD) */}
              <section>
                <h2 className="text-2xl font-bold mb-6">Frequently Asked Questions</h2>
                <div className="space-y-4">
                  {faqItems.map((faq, i) => (
                    <Card key={i}>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-base">{faq.question}</CardTitle>
                      </CardHeader>
                      <CardContent className="text-sm text-muted-foreground">{faq.answer}</CardContent>
                    </Card>
                  ))}
                </div>
              </section>

              <NewsletterSignup
                source="ai_economy_jobs"
                title="Track the AI Economy"
                description="Quarterly wage reports on AI-economy careers and the data behind them - straight to your inbox."
              />
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              <SidebarAd />

              <Card className="border-primary/20 bg-gradient-to-br from-primary/5 to-transparent">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <ShieldCheck className="h-5 w-5 text-primary" />
                    Will AI take your job?
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <p className="text-sm text-muted-foreground">
                    This page shows where AI creates jobs. The other side matters too: check your
                    occupation&rsquo;s automation-risk score and timeline.
                  </p>
                  <Link to="/ai-career-analysis">
                    <Button className="w-full">
                      Check My AI Risk
                      <ArrowRight className="h-4 w-4 ml-1" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Keep Exploring</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <Link
                    to="/insights/ai-wage-premium"
                    className="block text-sm text-muted-foreground hover:text-primary transition-colors"
                  >
                    The AI Wage Premium: Do AI Skills Pay More? →
                  </Link>
                  <Link
                    to="/no-degree-jobs"
                    className="block text-sm text-muted-foreground hover:text-primary transition-colors"
                  >
                    High-Paying Jobs Without a Degree →
                  </Link>
                  <Link
                    to="/in-demand-careers"
                    className="block text-sm text-muted-foreground hover:text-primary transition-colors"
                  >
                    In-Demand Careers & Shortages →
                  </Link>
                  <Link
                    to="/resources/ai-career-planning"
                    className="block text-sm text-muted-foreground hover:text-primary transition-colors"
                  >
                    AI Career Planning Resources →
                  </Link>
                  <Link
                    to="/trends"
                    className="block text-sm text-muted-foreground hover:text-primary transition-colors"
                  >
                    Wage Trends Explorer →
                  </Link>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Get AI-Ready</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <p className="text-sm text-muted-foreground">
                    Workers with AI skills earn a documented wage premium. Training is the raise lever.
                  </p>
                  <AffiliateCta provider="coursera" occupation="AI Skills" type="training" className="w-full" />
                  <AffiliateCta
                    provider="ziprecruiter"
                    occupation="AI Jobs"
                    location={isStatePage ? areaName : undefined}
                    type="job-search"
                    className="w-full"
                  />
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Data Sources</CardTitle>
                </CardHeader>
                <CardContent className="text-sm text-muted-foreground space-y-2">
                  <ul className="list-disc list-inside space-y-1">
                    <li>BLS OEWS wage data{hasLiveWages ? ' (live)' : ''}</li>
                    <li>BLS Employment Projections 2024-34</li>
                    <li>White House AI Action Plan</li>
                    <li>PwC AI Jobs Barometer (2026)</li>
                  </ul>
                  <Link to="/data-sources" className="text-primary hover:underline inline-block mt-2">
                    View all data sources →
                  </Link>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AIEconomyJobs;
